import { useEffect, useState } from "react";
import { Box, Table, TableRow, TableBody, TableHead, TableContainer, Pagination, Collapse, IconButton, Chip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import Button from "components/atoms/Button";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { MODAL, modalActions } from "redux/slices/modal";
import AssetManagementService from "services/assetManagement.service";
import { assetManagementActions } from "redux/slices/assetManagement/assetManagementSlice";
import TableLoadingWrapper from "components/templates/TableLoadingWrapper";
import { StyledTableCell, StyledTableRow } from "components/templates/Tables";
import BlockComponent from "components/organisms/BlockComponent/BlockComponent";

export default function HandoverStatus() {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((s) => s.assetManagement.loading);
  const handoverData = useAppSelector((s) => s.assetManagement.handoverData) as any[];
  const handoverCount = useAppSelector((s) => s.assetManagement.handoverCount) || 0;
  const handoverFilters = useAppSelector((s) => s.assetManagement.handoverFilters);
  const handoverRefresh = useAppSelector((s) => s.assetManagement.handoverRefresh) || 0;
  const handoverRefreshLoader = useAppSelector((s) => s.assetManagement.handoverRefreshLoader) || false;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    return () => {
      dispatch(assetManagementActions.resetHandoverPage());
    };
  }, [dispatch]);

  useEffect(() => {
    if (handoverFilters) {
      AssetManagementService.getGroupedAssetsSelection(handoverFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoverRefresh]);

  const handleToggle = (categoryId: string) => {
    setExpanded((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleAddCategory = () => {
    dispatch(
      modalActions.openModal({
        type: MODAL.HANDOVER_CATEGORY,
        data: {},
        width: "480px",
      })
    );
  };

  const handleAddSubCategory = () => {
    dispatch(
      modalActions.openModal({
        type: MODAL.HANDOVER_SUBCATEGORY,
        data: {},
        width: "520px",
      })
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
        <Button variant="contained" onClick={handleAddCategory}>
          Add Handover Category
        </Button>
        <Button variant="outlined" onClick={handleAddSubCategory}>
          Add Sub Category
        </Button>
      </Box>

      <div className="pagination-list">
        <p>{handoverCount > 0 && `${handoverCount} Handover Categor${handoverCount > 1 ? "ies" : "y"}`}</p>
        {handoverCount > 0 && handoverFilters && (
          <Pagination
            page={handoverFilters.page}
            disabled={handoverRefreshLoader}
            count={Math.ceil(handoverCount / handoverFilters.page_size)}
            onChange={(_e, page) => dispatch(assetManagementActions.setHandoverPage(page))}
          />
        )}
      </div>

      <TableContainer>
        <Table
          aria-label="handover table"
          sx={{
            minWidth: "100%",
            borderSpacing: "0 10px",
            borderBottomWidth: "0px",
            borderCollapse: "separate",
          }}
        >
          <TableLoadingWrapper
            coloumns={4}
            loading={loading}
            length={handoverRefreshLoader ? 0 : handoverData.length}
            message="No handover categories found"
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>Category Name</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Subcategories</StyledTableCell>
                <BlockComponent permission={"dynamics:edit"}>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </BlockComponent>
              </TableRow>
            </TableHead>
            <TableBody>
              {handoverData.map((cat: any) => {
                const isExpanded = expanded[cat._id] ?? false;
                return (
                  <>
                    <StyledTableRow key={cat._id}>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggle(cat._id)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <span>{cat.name}</span>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip 
                          size="small" 
                          label={cat.is_active ? "Active" : "Inactive"}
                          sx={{
                            backgroundColor: cat.is_active ? "rgba(76, 175, 80, 0.12)" : "rgba(158, 158, 158, 0.12)",
                            color: cat.is_active ? "rgba(76, 175, 80, 0.9)" : "rgba(158, 158, 158, 0.8)",
                            border: cat.is_active ? "1px solid rgba(76, 175, 80, 0.3)" : "1px solid rgba(158, 158, 158, 0.3)",
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {cat.subcategories?.length > 0 ? (
                          <Chip 
                            size="small" 
                            label={`${cat.subcategories.length} subcategor${cat.subcategories.length > 1 ? "ies" : "y"}`}
                            variant="outlined"
                          />
                        ) : (
                          <span style={{ color: "#999" }}>No subcategories</span>
                        )}
                      </StyledTableCell>
                      <BlockComponent permission={"dynamics:edit"}>
                        <StyledTableCell align="right">
                          <Button
                            size="small"
                            variant="text"
                            onClick={() =>
                              dispatch(
                                modalActions.openModal({
                                  type: MODAL.HANDOVER_CATEGORY,
                                  data: { category: cat },
                                  width: "480px",
                                })
                              )
                            }
                          >
                            Edit
                          </Button>
                        </StyledTableCell>
                      </BlockComponent>
                    </StyledTableRow>
                    {cat.subcategories && cat.subcategories.length > 0 && (
                      <StyledTableRow>
                        <StyledTableCell 
                          colSpan={4} 
                          sx={{ paddingBottom: 0, paddingTop: 0, border: "none" }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, pl: 6 }}>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                                {cat.subcategories.map((sc: any) => (
                                  <Chip 
                                    key={sc._id}
                                    label={
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <span>{sc.name}</span>
                                        <BlockComponent permission={"dynamics:edit"}>
                                          <IconButton
                                            size="small"
                                            sx={{ 
                                              p: 0.25,
                                              minWidth: "auto",
                                              width: "18px",
                                              height: "18px",
                                              color: sc.is_active ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.4)",
                                              "&:hover": { 
                                                backgroundColor: "rgba(0, 0, 0, 0.08)",
                                                color: "primary.main"
                                              }
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              dispatch(
                                                modalActions.openModal({
                                                  type: MODAL.HANDOVER_SUBCATEGORY,
                                                  data: { subCategory: sc },
                                                  width: "520px",
                                                })
                                              );
                                            }}
                                          >
                                            <EditIcon sx={{ fontSize: "12px" }} />
                                          </IconButton>
                                        </BlockComponent>
                                      </Box>
                                    }
                                    size="small"
                                    sx={{ 
                                      cursor: "default",
                                      backgroundColor: sc.is_active ? "rgba(76, 175, 80, 0.12)" : "rgba(158, 158, 158, 0.12)",
                                      color: sc.is_active ? "rgba(76, 175, 80, 0.9)" : "rgba(158, 158, 158, 0.8)",
                                      border: sc.is_active ? "1px solid rgba(76, 175, 80, 0.3)" : "1px solid rgba(158, 158, 158, 0.3)",
                                      "& .MuiChip-label": {
                                        paddingRight: "4px",
                                        paddingLeft: "8px"
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Collapse>
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </TableLoadingWrapper>
        </Table>
      </TableContainer>
    </Box>
  );
}

